import { useEffect, useMemo, useRef, useState } from 'react';

import type { ValidationState } from '@ui-component-library/types/status';

import { SuffixIcon } from '@ui-component-library/components/_deprecated/Input/SuffixIcon/SuffixIcon';

import { useDebounce } from '@ui-component-library/hooks/useDebounce';

import { Icon } from '../../Icon';
import { Tooltip } from '../../Tooltip';

import type { TextareaProps } from './Textarea.types';

import { S } from './Textarea.styles';

export const Textarea = ({
  variant = 'default',
  name,
  label,
  description,
  placeholder,
  value = '',
  disabled = false,
  required = false,
  minRows = 3,
  idleText,
  errorText,
  successText,
  isValid,
  validation,

  onChange,
  tabIndex = 0
}: TextareaProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [validationState, setValidationState] = useState<ValidationState>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedValue = useDebounce(inputValue, 1000);
  const effectiveValue = validation ? debouncedValue : inputValue;

  const helperTexts = {
    idle: idleText,
    error: errorText,
    success: successText
  };

  const isTyping = validation ? inputValue !== effectiveValue : false;
  const isFluid = variant === 'fluid';
  const [isLabelFloating, setIsLabelFloating] = useState(!!inputValue);

  const isValueValid = useMemo(() => {
    if (!effectiveValue) {
      return required ? false : true;
    }
    return validation ? validation(effectiveValue) : true;
  }, [effectiveValue, validation, required]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!validation) {
      return;
    }

    if (isValid !== undefined && !effectiveValue) {
      setValidationState(isValid ? 'success' : 'error');
      return;
    }

    if (effectiveValue) {
      setValidationState(isValueValid ? 'success' : 'error');
    }
  }, [effectiveValue, isValueValid, isValid, validation]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textareaValue = e.target.value;
    setInputValue(textareaValue);

    // Call onChange immediately for non-validation cases
    if (!validation) {
      onChange?.(textareaValue, true, name);
    }
  };

  useEffect(() => {
    if (!validation) {
      return;
    }

    // Only call onChange when user has finished typing (debounced)
    if (inputValue !== value) {
      onChange?.(effectiveValue, isValueValid, name);
    }
  }, [effectiveValue, validation, inputValue, value, onChange, isValueValid, name]);

  const handleFocus = () => {
    setIsFocused(true);
    if (isFluid) {
      setIsLabelFloating(true);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (isFluid && !inputValue) {
      setIsLabelFloating(false);
    }

    if (!validation) {
      return;
    }

    if (required && !inputValue) {
      setValidationState('error');
      return;
    }

    if (inputValue) {
      setValidationState(isValueValid ? 'success' : 'error');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setInputValue('');
    setValidationState('idle');

    // Immediately notify of clear action - don't wait for debounce
    onChange('', false, name);

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  const shouldShowHelperText = () => {
    if (disabled) {
      return false;
    }

    if (isValid === false && errorText) {
      return true;
    }

    return (
      validation && (validationState === 'error' || isFocused) && !!helperTexts[validationState]
    );
  };

  return (
    <S.Wrapper variant={variant} disabled={disabled || false}>
      {!isFluid ||
        (!label && (
          <S.TopSection>
            <S.Label htmlFor={name}>
              {label}
              {required && <>*</>}
            </S.Label>
            {!disabled && <S.Description>{description}</S.Description>}
          </S.TopSection>
        ))}
      <S.TextareaContainer $state={disabled ? 'disabled' : validationState}>
        {isFluid && (
          <S.FluidLabel $isFloating={isLabelFloating} htmlFor={name}>
            <Tooltip content={description} aria-label={`More information about ${label}`}>
              {label}
              {required && <>*</>}

              <S.FluidLabelIcon>
                <Icon name="InfoFilled" size="small" color="charcoal-500" aria-hidden="true" />
              </S.FluidLabelIcon>
            </Tooltip>
          </S.FluidLabel>
        )}
        <S.Textarea
          rows={minRows}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={isFluid ? '' : placeholder}
          $isFluid={isFluid}
          aria-describedby={description}
          tabIndex={tabIndex}
          value={inputValue || ''}
        />
        <S.SuffixWrapper>
          {(inputValue || !disabled) && (
            <SuffixIcon
              isTyping={isTyping}
              isFocused={isFocused}
              validationState={validationState}
              handleClear={handleClear}
            />
          )}
        </S.SuffixWrapper>
      </S.TextareaContainer>
      {shouldShowHelperText() && (
        <S.BottomSection>
          <S.HelperText $validationState={validationState}>
            {helperTexts[validationState]}
          </S.HelperText>
        </S.BottomSection>
      )}
    </S.Wrapper>
  );
};
